import React, { useEffect, useState } from "react";
import { authAPI } from "../services/api";
import "./LoginModal.css";

const LoginModal = ({ isOpen, onClose, onLoginSuccess, onForgotPassword }) => {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setMode("login");
      setPassword("");
      setConfirmPassword("");
      setError("");
      setMessage("");
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    if (mode === "signup" && password !== confirmPassword) {
      setLoading(false);
      setError("Passwords do not match.");
      return;
    }

    try {
      if (mode === "login") {
        const response = await authAPI.login({ email, password });
        onLoginSuccess?.(response.data || null);
        onClose?.();
      } else {
        const response = await authAPI.signup({ email, password });
        const payload = response.data || null;
        if (payload?.accessToken) {
          onLoginSuccess?.(payload);
          onClose?.();
          return;
        }

        setMessage(
          payload?.message
          || "Account created. If confirmation is required, check your email before signing in.",
        );
        setMode("login");
        setPassword("");
        setConfirmPassword("");
      }
    } catch (loginError) {
      setError(
        loginError?.response?.data?.error
        || (mode === "login"
          ? "Login failed. Check email/password."
          : "Unable to create account."),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-modal-overlay" role="dialog" aria-modal="true" aria-label="Login">
      <div className="login-modal-card">
        <div className="login-modal-header">
          <h3>{mode === "login" ? "Sign In" : "Create Account"}</h3>
          <button type="button" className="login-modal-close-btn" onClick={onClose}>
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-modal-form">
          <label htmlFor="login-email">
            Email
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@episd.org"
              required
              autoComplete="email"
            />
          </label>

          <label htmlFor="login-password">
            Password
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Your password"
              required
              autoComplete="current-password"
            />
          </label>

          {mode === "login" && (
            <div className="login-modal-inline-actions">
              <button
                type="button"
                className="login-modal-link-btn login-modal-link-btn-quiet"
                onClick={onForgotPassword}
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>
          )}

          {mode === "signup" && (
            <label htmlFor="login-confirm-password">
              Confirm Password
              <input
                id="login-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
              />
            </label>
          )}

          {error && <p className="login-modal-error">{error}</p>}
          {message && <p className="login-modal-message">{message}</p>}

          <div className="login-modal-actions">
            <button
              type="button"
              className="login-modal-link-btn"
              onClick={() => {
                setMode((prev) => (prev === "login" ? "signup" : "login"));
                setError("");
                setMessage("");
                setPassword("");
                setConfirmPassword("");
              }}
              disabled={loading}
            >
              {mode === "login" ? "Create account" : "Back to sign in"}
            </button>
            <button type="submit" className="primary-btn" disabled={loading}>
              {loading
                ? (mode === "login" ? "Signing in..." : "Creating...")
                : (mode === "login" ? "Sign In" : "Create Account")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
